import jsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment";
import { formatFeeName } from "@/utilities/settings";
import { getFullSchoolName, getSchoolTypeLabel } from "@/utilities/buildingTypeLabel";
import writtenNumber from "written-number";
import { i18n } from "@/localization";
import apiClient from "@/services/axios";
import {
  BASE_REDUCTION_DISPLAY,
  TITLE_MODE,
  docTypeFromTypeFR,
  resolveDocumentTemplate,
} from "@/utilities/documentTemplateConfig";

/** Main grid table header (Ant Design primary) */
const TABLE_HEAD_FILL = [24, 144, 255];
const TABLE_HEAD_TEXT = 255;

/**
 * Map i18n locale to written-number language code
 */
function getWrittenNumberLang() {
  const locale = i18n.locale || "fr-FR";
  const localeMap = {
    "fr-FR": "fr",
    "en-US": "en",
    "ar-AR": "ar",
    "sw-KE": "en",
  };
  return localeMap[locale] || "fr";
}

function numberToWords(num) {
  const lang = getWrittenNumberLang();
  const [integerPart, decimalPart] = Number(num || 0)
    .toString()
    .split(".");
  let words = writtenNumber(parseInt(integerPart, 10) || 0, { lang });

  if (decimalPart) {
    const trimmedDecimal = decimalPart.replace(/0+$/, "");
    if (trimmedDecimal.length > 0) {
      words +=
        " " +
        i18n.t("invoice.dinarsEt") +
        " " +
        trimmedNumberToWords(trimmedDecimal) +
        " " +
        i18n.t("invoice.millimes");
    }
  }

  return words;
}

function trimmedNumberToWords(decimalStr) {
  const lang = getWrittenNumberLang();
  return decimalStr
    .split("")
    .map((digit) => writtenNumber(parseInt(digit, 10), { lang }))
    .join(" ");
}

function resolveSchoolTitle(schoolDetails, template) {
  if (template.titleMode === TITLE_MODE.CUSTOM && template.customTitle) {
    return template.customTitle;
  }
  if (!template.includeSchoolTypeInTitle) {
    return (
      schoolDetails?.legalName ||
      schoolDetails?.name ||
      getSchoolTypeLabel(schoolDetails) ||
      ""
    );
  }
  return getFullSchoolName(schoolDetails, "legalName");
}

/**
 * Real class / activity name for PDF designation.
 * Ticket: show class even when classroom.enrollmentConfirmed === false
 * ("indisponible aux parents") — never replace with an "unavailable" label.
 */
function resolveClassOrActivityName(item) {
  const payment = item?.payment;
  if (payment && typeof payment === "object") {
    if (item.typeAS === "A" || payment.typeAS === "A") {
      const activity = payment.activity;
      if (activity && typeof activity === "object" && activity.designation) {
        return activity.designation;
      }
      if (payment.name) return payment.name;
    } else {
      const classroom = payment.classroom;
      if (classroom && typeof classroom === "object" && classroom.name) {
        return classroom.name;
      }
      if (payment.name) return payment.name;
    }
  }

  const raw = item?.typeReglement || "";
  const slash = raw.indexOf("/");
  return slash >= 0 ? raw.slice(slash + 1).trim() : "";
}

function formatDesignation(item, template) {
  const raw = item?.typeReglement || "";
  const slash = raw.indexOf("/");
  const prefix =
    slash >= 0
      ? raw.slice(0, slash)
      : item?.typeAS === "A" || item?.payment?.typeAS === "A"
        ? "Activité"
        : "Scolarité";

  if (!template.showClass) {
    return prefix || raw;
  }

  const name = resolveClassOrActivityName(item);
  if (name) return `${prefix}/${name}`;
  return raw || prefix;
}

function paymentMethodLabel(code) {
  const key = `recette.paymentMethod.${code}`;
  const translated = i18n.t(key);
  return translated === key ? code : translated;
}

function collectBaseReductionLines(record) {
  const lines = [];
  let totalPercentSum = 0;
  let count = 0;
  (record.details || []).forEach((item) => {
    const rep = item.payment?.repReduction || {};
    Object.keys(item.fees || {}).forEach((feeKey) => {
      const feeReduction =
        item.fees[feeKey]?.reduction ??
        (typeof rep[feeKey] === "number" ? rep[feeKey] : null);
      if (feeReduction && Number(feeReduction) !== 0) {
        lines.push({
          label:
            formatFeeName(feeKey, item.daily, i18n.t.bind(i18n)) +
            (item.typeReglement ? ` (${item.typeReglement})` : ""),
          percent: Number(feeReduction),
        });
        totalPercentSum += Number(feeReduction);
        count += 1;
      }
    });
  });
  return { lines, totalPercentSum, count };
}

function loadLogoImage(settings) {
  const arrayOfImages = settings?.image || [];
  for (let i = 0; i < arrayOfImages.length; i++) {
    if (settings.activeBuilding === arrayOfImages[i].db) {
      const imgData = new Image();
      imgData.src = arrayOfImages[i].logo;
      return imgData;
    }
  }
  return null;
}

async function loadStampUrl(schoolDetails, settings) {
  if (!schoolDetails?._id) return null;
  try {
    const res = await apiClient.get(`/buildings/${schoolDetails._id}/stamp`);
    const path = res?.data?.imageURL;
    if (!path) return null;
    // Same URL construction as Paramétrage → Établissement → Autre (cachet)
    if (settings?.resourcesUrl) {
      return settings.resourcesUrl.replace("FILE_PATH", path);
    }
    return path;
  } catch (e) {
    console.log(e);
    return null;
  }
}

function stampImageFormat(url) {
  const lower = (url || "").toLowerCase();
  if (lower.includes(".png") || lower.includes("image/png")) return "PNG";
  if (lower.includes(".webp")) return "WEBP";
  return "JPEG";
}

/**
 * Load stamp image for jsPDF (must be fully loaded before addImage).
 * @returns {Promise<{img: HTMLImageElement, format: string}|null>}
 */
function loadStampImageElement(stampAsset) {
  return new Promise((resolve) => {
    if (!stampAsset) {
      resolve(null);
      return;
    }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve({ img, format: stampImageFormat(stampAsset) });
    img.onerror = () => resolve(null);
    img.src = stampAsset;
  });
}

function generateHeader(doc, record, settings, y, schoolDetails, template) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const isArabic = i18n.locale === "ar-AR";

  if (template.showLogo) {
    const imgData = loadLogoImage(settings);
    try {
      if (imgData?.src) {
        const logoX = isArabic ? pageWidth - 30 - 50 : 30;
        doc.addImage(imgData, "JPEG", logoX, y, 50, 50);
      }
    } catch (error) {
      console.log(error);
    }
  }

  doc.setFontSize(14);
  doc.setFont("Amiri", "Bold");
  const schoolName = resolveSchoolTitle(schoolDetails, template);
  const schoolNameWidth = doc.getTextWidth(schoolName || "");
  const schoolNameX = isArabic ? 30 : pageWidth - 30 - schoolNameWidth;
  doc.text(schoolName || "", schoolNameX, y + 30);

  doc.setFont("Amiri", "normal");
  doc.setFontSize(18);
  y += 50;

  doc.setFontSize(10);
  const infoWidth = 180;
  const infoHeight = schoolDetails?.MF && record.typeFR == "F" ? 53 : 38;
  const infoStartX = isArabic ? pageWidth - 35 - infoWidth : 35;
  const infoStartY = y + 32;
  doc.rect(infoStartX, infoStartY, infoWidth, infoHeight);

  const infoLabelX = isArabic ? pageWidth - 35 - infoWidth + 5 : 40;
  const infoValueX = isArabic ? pageWidth - 35 - infoWidth + 60 : 100;

  doc.text(i18n.t("invoice.numeroLabel"), infoLabelX, y + 45);
  doc.text(record.serial || "--", infoValueX, y + 45);
  doc.text(i18n.t("invoice.dateLabel"), infoLabelX, y + 60);
  doc.text(
    record.date ? moment(record.date).format("DD/MM/YYYY") : "--",
    infoValueX,
    y + 60
  );

  if (schoolDetails?.MF && record.typeFR == "F") {
    doc.text(i18n.t("invoice.mf") + " :", infoLabelX, y + 75);
    doc.text(schoolDetails.MF, infoValueX, y + 75);
  }

  doc.setFontSize(12);
  const addressedToX = isArabic ? 30 : pageWidth - 200;
  doc.text(i18n.t("invoice.adresseeA"), addressedToX, y + 30);
  doc.setFont("Amiri", "normal");
  doc.text(record.addressedTo?.name || "", addressedToX, y + 50);
  doc.setFontSize(10);
  if (record.addressedTo?.type === "ENTREPRISE") {
    doc.text(
      i18n.t("invoice.mf") + " : " + (record.addressedTo.MF || ""),
      addressedToX,
      y + 70
    );
  }

  return y + 20;
}

function currentPageNumber(doc) {
  const info = doc.internal.getCurrentPageInfo
    ? doc.internal.getCurrentPageInfo()
    : null;
  return info?.pageNumber || doc.internal.getNumberOfPages();
}

/**
 * Footer is fixed at the bottom of every page. Drawn at most once per
 * (panel, page) so repeated hooks never stack text on top of itself.
 * Stamp / signature are NOT drawn here — only on the last page via
 * drawStampAndSignature (avoids them on page 1 of multi-page docs).
 */
function generateFooter(doc, schoolDetails, template, stampAsset) {
  const footerKey = `${doc.__edtrustPanelKey ?? "s"}:${currentPageNumber(doc)}`;
  if (!doc.__edtrustFooterPages) doc.__edtrustFooterPages = new Set();
  if (doc.__edtrustFooterPages.has(footerKey)) return;
  doc.__edtrustFooterPages.add(footerKey);

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const isArabic = i18n.locale === "ar-AR";
  const footerStartY = pageHeight - 80;

  doc.setFontSize(9);
  doc.setFont("Amiri", "normal");

  doc.setDrawColor(0, 0, 0);
  doc.line(30, footerStartY + 15, pageWidth - 30, footerStartY + 15);

  const footerSchoolName = resolveSchoolTitle(schoolDetails, template);
  const schoolNameX = isArabic
    ? pageWidth - 30 - doc.getTextWidth(footerSchoolName || "")
    : 30;
  const addressX = isArabic
    ? pageWidth - 30 - doc.getTextWidth(schoolDetails?.adress || "")
    : 30;
  doc.text(footerSchoolName || "", schoolNameX, footerStartY + 30);
  doc.text(schoolDetails?.adress || "", addressX, footerStartY + 45);

  const phoneText = `${i18n.t("invoice.tel")}: ${schoolDetails?.phone || ""}`;
  const emailText = `${i18n.t("invoice.mail")}: ${schoolDetails?.email || ""}`;
  const phoneX = isArabic ? 30 : pageWidth - 30 - doc.getTextWidth(phoneText);
  const emailX = isArabic ? 30 : pageWidth - 30 - doc.getTextWidth(emailText);
  doc.text(phoneText, phoneX, footerStartY + 30);
  doc.text(emailText, emailX, footerStartY + 45);

  if (template.showFooterText && template.footerText) {
    doc.setFontSize(8);
    const customY = footerStartY + 58;
    const lines = doc.splitTextToSize(template.footerText, pageWidth - 60, { fontSize: 8 });
    doc.text(lines, 30, customY);
  }
}

/**
 * Cachet / stamp / signature — only on the last page of the document
 * (or of the current dual panel). Call once after all content is drawn.
 */
function drawStampAndSignature(doc, schoolDetails, template, stampAsset) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const isArabic = i18n.locale === "ar-AR";
  const stampLoaded = Boolean(template.showStamp && stampAsset?.img);
  const footerStartY = pageHeight - 80;

  doc.setFontSize(9);
  doc.setFont("Amiri", "normal");

  if (template.showCachetSignatureLabel && !stampLoaded) {
    const cachetText = i18n.t("invoice.cachetSignature");
    const cachetX = isArabic ? 30 : pageWidth - 150;
    doc.text(cachetText, cachetX, footerStartY);
  }

  if (stampLoaded) {
    try {
      const scale = Number(template.stampScale) || 1;
      const baseSize = 55;
      const size = baseSize * scale;
      const stampX = isArabic ? 30 : pageWidth - 30 - size;
      doc.addImage(
        stampAsset.img,
        stampAsset.format || "PNG",
        stampX,
        footerStartY - size + 10,
        size,
        size
      );
    } catch (e) {
      console.log(e);
    }
  }

  if (template.showSignature) {
    const sigX = isArabic ? pageWidth - 160 : 40;
    doc.setDrawColor(120, 120, 120);
    doc.rect(sigX, footerStartY - 40, 90, 35);
    doc.setFontSize(8);
    doc.text(i18n.t("invoice.signature"), sigX + 4, footerStartY - 28);
    doc.setDrawColor(0, 0, 0);
  }
}

/** Shared autoTable options so secondary tables paginate above the footer too. */
function footerAwareTableOptions(doc, template) {
  return {
    margin: { left: 30, right: 30, bottom: getFooterReservedHeight(template) },
    pageBreak: "auto",
    didDrawPage: () => {
      if (typeof doc.__edtrustDrawFooter === "function") {
        doc.__edtrustDrawFooter();
      }
    },
  };
}

function getFooterReservedHeight(template) {
  let h = 100;
  if (template?.showFooterText && template.footerText) h += 24;
  if (template?.showStamp || template?.showSignature) {
    const scale = Number(template.stampScale) || 1;
    h += Math.max(20, 55 * scale * 0.4);
  }
  return h;
}

function drawPaymentMethodBlock(doc, record, template, startY, isArabic, pageWidth) {
  if (!template.showPaymentMethodDetails) return startY;

  const filled = (v) => (v && String(v).trim() !== "--" ? String(v).trim() : "");
  // Cheque / traite / virement documents, grouped by method; unpopulated
  // ObjectIds carry no detail and are ignored.
  const detailsByMethod = {};
  (record.cheques || []).forEach((c) => {
    if (!c || typeof c !== "object") return;
    const type = c.type || "CHEQUE";
    (detailsByMethod[type] = detailsByMethod[type] || []).push(c);
  });
  const field = (labelKey, value) =>
    value ? `${i18n.t(labelKey)} : ${value}` : "";
  const fmtDate = (d) => (d ? moment(d).format("DD/MM/YYYY") : "");
  // Bank account (Caisse) the transfer / TPE was received on, when populated
  const caisseBankName = (cb) =>
    cb && typeof cb === "object" ? filled(cb.bank) || filled(cb.name) : "";

  const detailRow = (c) => {
    const type = c.type || "CHEQUE";
    const num = filled(c.num || c.numero);
    const holder = filled(c.titulaire || c.holder);
    let parts;
    if (type === "VIREMENT") {
      parts = [
        field("invoice.pmReference", num),
        field("invoice.pmHolder", holder),
        field("invoice.pmBank", filled(c.bank) || caisseBankName(c.caisseBank)),
        field("invoice.pmRib", filled(c.rib)),
        field("invoice.pmDate", fmtDate(c.dateEchance || record.date)),
      ];
    } else {
      // CHEQUE / TRAITE
      parts = [
        field("invoice.pmNumber", num),
        field("invoice.pmHolder", holder),
        field("invoice.pmBank", filled(c.bank)),
        type === "TRAITE" ? field("invoice.pmRib", filled(c.rib)) : "",
        field("invoice.pmDueDate", fmtDate(c.dateEchance)),
      ];
    }
    const label = [paymentMethodLabel(type), ...parts.filter(Boolean)].join(" — ");
    const amountStr = c.montant != null ? Number(c.montant).toFixed(3) : "";
    return [label, amountStr];
  };

  const rows = [];
  const methods = record.paymentMethod || [];
  methods.forEach((m) => {
    // Details replace the method line so each amount is listed once
    if (detailsByMethod[m]?.length) {
      detailsByMethod[m].forEach((c) => rows.push(detailRow(c)));
      delete detailsByMethod[m];
      return;
    }
    let amount = null;
    let extra = "";
    if (m === "ESPESE") amount = record.montantEspece;
    if (m === "CHEQUE") amount = record.montantCheque;
    if (m === "TPE") {
      amount = record.montantTPE ?? record.tpe?.montant;
      const tpeParts = [
        field("invoice.transactionCode", filled(record.tpe?.transactionId)),
        field("invoice.pmBank", caisseBankName(record.tpe?.caisseBank)),
      ].filter(Boolean);
      if (tpeParts.length) extra = ` — ${tpeParts.join(" — ")}`;
    }
    if (m === "TRAITE") amount = record.montantTraite;
    if (m === "VIREMENT") amount = record.montantVirement;
    const amountStr =
      amount != null && amount !== ""
        ? Number(amount).toFixed(3)
        : "";
    rows.push([`${paymentMethodLabel(m)}${extra}`, amountStr]);
  });

  // Documents whose method is not listed in paymentMethod (legacy data)
  Object.values(detailsByMethod).forEach((list) =>
    list.forEach((c) => rows.push(detailRow(c)))
  );

  if (!rows.length) return startY;

  const body = isArabic ? rows.map((r) => [r[1], r[0]]) : rows;
  doc.autoTable({
    startY,
    head: [
      isArabic
        ? ["", i18n.t("invoice.paymentMethods")]
        : [i18n.t("invoice.paymentMethods"), ""],
    ],
    body,
    styles: { fontSize: 8, font: "Amiri", halign: isArabic ? "right" : "left" },
    theme: "grid",
    headStyles: {
      fillColor: TABLE_HEAD_FILL,
      textColor: TABLE_HEAD_TEXT,
      halign: isArabic ? "right" : "left",
    },
    tableWidth: pageWidth - 60,
    ...footerAwareTableOptions(doc, template),
  });
  return doc.lastAutoTable.finalY + 8;
}

const SUMMARY_SUBHEAD_FILL = [230, 242, 255];

function summaryLineLabel(line) {
  let label = formatFeeName(line.feeKey, line.daily, i18n.t.bind(i18n));
  if (line.name && line.typeAS === "A") label = `${line.name} — ${label}`;
  if (line.student) label = `${line.student} · ${label}`;
  if (line.partial) label += ` (${i18n.t("invoice.summaryPartial")})`;
  return label;
}

/**
 * One payment summary section (tuition or activity):
 * "Total to pay" on top, Debit (paid) | Credit (unpaid) columns, totals row.
 */
function drawPaymentSummarySection(doc, section, title, typeAS, template, startY, isArabic, pageWidth) {
  if (!section || !(Number(section.totalToPay) > 0)) return startY;

  const money = (n) => Number(n || 0).toFixed(3);
  const paid = (section.paid || []).map((l) => ({ ...l, typeAS }));
  const unpaid = (section.unpaid || []).map((l) => ({ ...l, typeAS }));
  const rowCount = Math.max(paid.length, unpaid.length);

  const orient = (row) => (isArabic ? [...row].reverse() : row);
  const body = [];
  for (let i = 0; i < rowCount; i++) {
    const p = paid[i];
    const u = unpaid[i];
    body.push(
      orient([
        p ? summaryLineLabel(p) : "",
        p ? money(p.amount) : "",
        u ? summaryLineLabel(u) : "",
        u ? money(u.amount) : "",
      ])
    );
  }

  const totalToPayText = `${i18n.t("invoice.summaryTotalToPay")} : ${money(section.totalToPay)}`;
  const titleCells = [
    { content: title, colSpan: 2, styles: { halign: isArabic ? "right" : "left" } },
    { content: totalToPayText, colSpan: 2, styles: { halign: isArabic ? "left" : "right" } },
  ];
  const subHead = orient([
    { content: i18n.t("invoice.summaryDebitPaid"), colSpan: 2 },
    { content: i18n.t("invoice.summaryCreditUnpaid"), colSpan: 2 },
  ]);
  const totals = orient([
    i18n.t("invoice.summaryTotalPaid"),
    money(section.totalPaid),
    i18n.t("invoice.summaryTotalUnpaid"),
    money(section.totalUnpaid),
  ]);

  const amountCols = isArabic ? [0, 2] : [1, 3];
  const amountWidth = 70;

  doc.autoTable({
    startY,
    head: [isArabic ? [...titleCells].reverse() : titleCells, subHead],
    body,
    foot: [totals],
    showHead: "everyPage",
    showFoot: "lastPage",
    styles: { fontSize: 8, font: "Amiri", halign: isArabic ? "right" : "left" },
    theme: "grid",
    headStyles: {
      fillColor: TABLE_HEAD_FILL,
      textColor: TABLE_HEAD_TEXT,
      halign: isArabic ? "right" : "left",
    },
    footStyles: {
      fillColor: SUMMARY_SUBHEAD_FILL,
      textColor: 20,
      fontStyle: "bold",
    },
    columnStyles: {
      [amountCols[0]]: { cellWidth: amountWidth, halign: "right" },
      [amountCols[1]]: { cellWidth: amountWidth, halign: "right" },
    },
    didParseCell: (data) => {
      if (data.section === "head" && data.row.index === 1) {
        data.cell.styles.fillColor = SUMMARY_SUBHEAD_FILL;
        data.cell.styles.textColor = 20;
      }
      if (data.section === "foot" && amountCols.includes(data.column.index)) {
        data.cell.styles.halign = "right";
      }
    },
    tableWidth: pageWidth - 60,
    ...footerAwareTableOptions(doc, template),
  });
  return doc.lastAutoTable.finalY + 8;
}

function drawPaymentSummary(doc, record, template, startY, isArabic, pageWidth) {
  const summary = record.paymentSummary;
  if (!summary) return startY;
  let y = startY;
  if (template.showTuitionSummary) {
    y = drawPaymentSummarySection(
      doc, summary.tuition, i18n.t("invoice.tuitionSummary"), "S",
      template, y, isArabic, pageWidth
    );
  }
  if (template.showActivitySummary) {
    y = drawPaymentSummarySection(
      doc, summary.activity, i18n.t("invoice.activitySummary"), "A",
      template, y, isArabic, pageWidth
    );
  }
  return y;
}

const ARABIC_CHARS = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;

function hexToRgb(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || ""));
  if (!m) return [0, 0, 0];
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/**
 * School free text printed in the white space right below the last
 * payment/summary table (not part of the footer). Alignment is configurable;
 * "auto" right-aligns Arabic lines and left-aligns the rest.
 */
function drawTableNote(doc, template, startY, isArabic, pageWidth) {
  const text = template.showTableNote ? String(template.tableNoteText || "") : "";
  if (!text.trim()) return startY;

  const pageHeight = doc.internal.pageSize.getHeight();
  const bottomLimit = pageHeight - getFooterReservedHeight(template);
  const fontSize = Number(template.tableNoteFontSize) || 10;
  const lineHeight = fontSize * 1.25;
  doc.setFont("Amiri", "normal");
  doc.setFontSize(fontSize);
  doc.setTextColor(...hexToRgb(template.tableNoteColor));

  // Explicit fontSize: inside a dual panel the internal size is the scaled one
  const lines = doc.splitTextToSize(text, pageWidth - 60, { fontSize });
  let y = startY + fontSize;
  lines.forEach((line) => {
    if (y > bottomLimit) {
      doc.addPage();
      if (typeof doc.__edtrustDrawFooter === "function") doc.__edtrustDrawFooter();
      doc.setFont("Amiri", "normal");
      doc.setFontSize(fontSize);
      doc.setTextColor(...hexToRgb(template.tableNoteColor));
      y = 40;
    }
    let align = template.tableNoteAlign;
    if (!["left", "center", "right"].includes(align)) {
      align = isArabic || ARABIC_CHARS.test(line) ? "right" : "left";
    }
    const x = align === "center" ? pageWidth / 2 : align === "right" ? pageWidth - 30 : 30;
    doc.text(line, x, y, { align });
    y += lineHeight;
  });

  doc.setTextColor(0, 0, 0);
  return y;
}

const isRef = (v) => v != null && typeof v !== "object";

/**
 * Create responses / lean lists only carry ObjectIds for cheques and the
 * virement / TPE bank accounts.
 */
function needsPaymentDetailsReload(record) {
  const cheques = record.cheques || [];
  return (
    cheques.some((c) => !c || typeof c !== "object") ||
    cheques.some((c) => c && c.type === "VIREMENT" && isRef(c.caisseBank)) ||
    isRef(record.tpe?.caisseBank)
  );
}

async function loadPaymentDetails(invoiceId) {
  if (!invoiceId) return null;
  try {
    const res = await apiClient.get(`/compV3/parent-invoice/${invoiceId}`);
    if (!res?.data) return null;
    return { cheques: res.data.cheques, tpe: res.data.tpe };
  } catch (e) {
    console.error("Payment details unavailable:", e);
    return null;
  }
}

async function loadPaymentSummary(invoiceId) {
  if (!invoiceId) return null;
  try {
    const res = await apiClient.get(`/compV3/parent-invoice/${invoiceId}/payment-summary`);
    return res?.data || null;
  } catch (e) {
    console.log(e);
    return null;
  }
}

function drawBaseReductionBlock(doc, record, template, startY, isArabic, pageWidth) {
  if (template.baseReductionDisplay === BASE_REDUCTION_DISPLAY.HIDDEN) {
    return startY;
  }
  const { lines, totalPercentSum, count } = collectBaseReductionLines(record);
  if (!lines.length) return startY;

  let rows = [];
  if (template.baseReductionDisplay === BASE_REDUCTION_DISPLAY.TOTAL) {
    rows.push([
      i18n.t("invoice.baseReductionTotal"),
      count
        ? `${(totalPercentSum / count).toFixed(2)}% (${count})`
        : "",
    ]);
  } else {
    rows = lines.map((l) => [l.label, `${l.percent} %`]);
  }

  const body = isArabic ? rows.map((r) => [r[1], r[0]]) : rows;
  doc.autoTable({
    startY,
    head: [
      isArabic
        ? ["", i18n.t("invoice.baseReduction")]
        : [i18n.t("invoice.baseReduction"), ""],
    ],
    body,
    styles: { fontSize: 8, font: "Amiri", halign: isArabic ? "right" : "left" },
    theme: "grid",
    headStyles: {
      fillColor: TABLE_HEAD_FILL,
      textColor: TABLE_HEAD_TEXT,
      halign: isArabic ? "right" : "left",
    },
    tableWidth: pageWidth - 60,
    ...footerAwareTableOptions(doc, template),
  });
  return doc.lastAutoTable.finalY + 8;
}

function ensureSpace(doc, schoolDetails, template, stampAsset, needed, currentY) {
  const pageHeight = doc.internal.pageSize.getHeight();
  const footerReserved = getFooterReservedHeight(template);
  if (currentY + needed > pageHeight - footerReserved) {
    doc.addPage();
    generateFooter(doc, schoolDetails, template, stampAsset);
    return 20;
  }
  return currentY;
}

/**
 * autoTable offsets right/center text by `getStringUnitWidth * internal font
 * size`; inside a scaled panel that size is physical while x is logical, so the
 * text drifts right. Draw those cells with jsPDF's native alignment instead.
 */
function withPanelTextAlignment(doc, options = {}) {
  const userWillDrawCell = options.willDrawCell;
  const userDidDrawCell = options.didDrawCell;
  return {
    ...options,
    willDrawCell: (data) => {
      const result = userWillDrawCell ? userWillDrawCell(data) : undefined;
      const { halign } = data.cell.styles;
      if (
        result !== false &&
        (halign === "right" || halign === "center") &&
        Array.isArray(data.cell.text) &&
        data.cell.text.length
      ) {
        data.cell.__panelText = data.cell.text;
        data.cell.text = [];
      }
      return result;
    },
    didDrawCell: (data) => {
      const lines = data.cell.__panelText;
      if (lines) {
        data.cell.text = lines;
        delete data.cell.__panelText;
        const { halign, valign } = data.cell.styles;
        const pos = data.cell.getTextPos();
        const fontSize = doc.internal.getFontSize() / doc.internal.scaleFactor;
        const lineHeight = fontSize * 1.15;
        let y = pos.y + fontSize * (2 - 1.15);
        if (valign === "middle") y -= (lines.length / 2) * lineHeight;
        else if (valign === "bottom") y -= lines.length * lineHeight;
        lines.forEach((line) => {
          doc.text(line, pos.x, y, { align: halign });
          y += lineHeight;
        });
      }
      if (userDidDrawCell) userDidDrawCell(data);
    },
  };
}

/** A4 portrait width in pt (logical layout for dual-panel scaling) */
const A4_PORTRAIT_PT = { w: 595.28, h: 841.89 };

/**
 * Make the landscape doc behave like a full A4 portrait page whose drawing is
 * scaled + translated into one half of the sheet. autoTable ignores PDF CTM,
 * so we patch the drawing APIs it actually uses.
 *
 * Page breaks are real: when the panel content overflows, a new landscape
 * page is added (or reused if the other panel already created it) and the
 * panel continues at the same offset on that page.
 */
function installScaledPanel(doc, { panelIndex, offsetX, offsetY, scale }) {
  const pageSize = doc.internal.pageSize;
  const origGetWidth = pageSize.getWidth.bind(pageSize);
  const origGetHeight = pageSize.getHeight.bind(pageSize);
  pageSize.getWidth = () => A4_PORTRAIT_PT.w;
  pageSize.getHeight = () => A4_PORTRAIT_PT.h;
  // autoTable reads the `width`/`height` accessors, not getWidth()/getHeight()
  const origWidthDesc = Object.getOwnPropertyDescriptor(pageSize, "width");
  const origHeightDesc = Object.getOwnPropertyDescriptor(pageSize, "height");
  Object.defineProperty(pageSize, "width", {
    get: () => A4_PORTRAIT_PT.w,
    set: () => {},
    enumerable: true,
    configurable: true,
  });
  Object.defineProperty(pageSize, "height", {
    get: () => A4_PORTRAIT_PT.h,
    set: () => {},
    enumerable: true,
    configurable: true,
  });

  doc.__edtrustDualPanel = true;
  doc.__edtrustPanelKey = panelIndex;

  const sx = (x) => offsetX + Number(x) * scale;
  const sy = (y) => offsetY + Number(y) * scale;

  const origSetFontSize = doc.setFontSize.bind(doc);
  const origGetFontSize = doc.getFontSize.bind(doc);
  const physicalFontOnEnter = origGetFontSize();
  let logicalFontSize = 12;
  origSetFontSize(logicalFontSize * scale);

  doc.setFontSize = (size) => {
    logicalFontSize = size;
    return origSetFontSize(size * scale);
  };
  doc.getFontSize = () => logicalFontSize;

  const origGetTextWidth = doc.getTextWidth.bind(doc);
  doc.getTextWidth = (text) => origGetTextWidth(text) / scale;

  const origText = doc.text.bind(doc);
  doc.text = (text, x, y, options) => {
    if (typeof x === "object" && x !== null) {
      const opts = { ...x };
      if (typeof opts.x === "number") opts.x = sx(opts.x);
      if (typeof opts.y === "number") opts.y = sy(opts.y);
      return origText(text, opts);
    }
    if (y === undefined) {
      return origText(text, x);
    }
    return origText(text, sx(x), sy(y), options);
  };

  const origRect = doc.rect.bind(doc);
  doc.rect = (x, y, w, h, style) =>
    origRect(sx(x), sy(y), w * scale, h * scale, style);

  const origLine = doc.line.bind(doc);
  doc.line = (x1, y1, x2, y2) =>
    origLine(sx(x1), sy(y1), sx(x2), sy(y2));

  const origSetLineWidth = doc.setLineWidth.bind(doc);
  doc.setLineWidth = (w) => origSetLineWidth(w * scale);

  const origAddImage = doc.addImage.bind(doc);
  doc.addImage = (imageData, format, x, y, w, h, alias, compression, rotation) => {
    if (typeof format === "number") {
      return origAddImage(
        imageData,
        sx(format),
        sy(x),
        y * scale,
        w * scale,
        h,
        alias,
        compression
      );
    }
    return origAddImage(
      imageData,
      format,
      sx(x),
      sy(y),
      (w || 0) * scale,
      (h || 0) * scale,
      alias,
      compression,
      rotation
    );
  };

  const origAutoTable = doc.autoTable;
  doc.autoTable = (options) => origAutoTable.call(doc, withPanelTextAlignment(doc, options));

  // Real pagination: each panel walks its own page counter over the shared
  // landscape sheets. Page N is created by the first panel that reaches it.
  const origAddPage = doc.addPage.bind(doc);
  const origSetPage = doc.setPage.bind(doc);
  let panelPage = 1;
  origSetPage(1);
  doc.addPage = () => {
    panelPage += 1;
    if (doc.internal.getNumberOfPages() < panelPage) {
      origAddPage();
    } else {
      origSetPage(panelPage);
    }
    return doc;
  };

  return function restore() {
    pageSize.getWidth = origGetWidth;
    pageSize.getHeight = origGetHeight;
    if (origWidthDesc) Object.defineProperty(pageSize, "width", origWidthDesc);
    if (origHeightDesc) Object.defineProperty(pageSize, "height", origHeightDesc);
    doc.setFontSize = origSetFontSize;
    doc.getFontSize = origGetFontSize;
    doc.getTextWidth = origGetTextWidth;
    doc.text = origText;
    doc.rect = origRect;
    doc.line = origLine;
    doc.setLineWidth = origSetLineWidth;
    doc.addImage = origAddImage;
    doc.addPage = origAddPage;
    doc.autoTable = origAutoTable;
    origSetFontSize(physicalFontOnEnter);
    delete doc.__edtrustDualPanel;
    delete doc.__edtrustPanelKey;
  };
}

/**
 * Support: "2 copies / page" → landscape A4, same receipt left + right.
 * Each half is the normal A4 portrait layout scaled down. Long documents
 * paginate onto extra landscape pages exactly like the single-copy print.
 *
 * @param {(doc: import('jspdf').jsPDF) => void} drawPortraitContent
 * @returns {import('jspdf').jsPDF}
 */
function composeLandscapeDualCopies(drawPortraitContent) {
  const land = new jsPDF({
    unit: "pt",
    format: "a4",
    orientation: "landscape",
  });
  const landW = land.internal.pageSize.getWidth();
  const landH = land.internal.pageSize.getHeight();
  const halfW = landW / 2;
  const gutter = 14;
  const maxW = halfW - gutter * 1.5;
  const maxH = landH - 20;
  const scale = Math.min(maxW / A4_PORTRAIT_PT.w, maxH / A4_PORTRAIT_PT.h);
  const drawW = A4_PORTRAIT_PT.w * scale;

  for (let panel = 0; panel < 2; panel++) {
    const offsetX = panel * halfW + (halfW - drawW) / 2;
    const offsetY = 10;
    const restore = installScaledPanel(land, {
      panelIndex: panel,
      offsetX,
      offsetY,
      scale,
    });
    try {
      drawPortraitContent(land);
    } finally {
      restore();
    }
  }

  // Cut guide on every sheet
  const pageCount = land.internal.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    land.setPage(p);
    land.setDrawColor(160, 160, 160);
    land.setLineDashPattern([4, 4], 0);
    land.line(halfW, 8, halfW, landH - 8);
    land.setLineDashPattern([], 0);
    land.setDrawColor(0, 0, 0);
  }

  return land;
}

function finishDoc(doc, fileName, options) {
  if (options.output === "blob") {
    return doc.output("blob");
  }
  doc.save(fileName);
}

async function refreshBuildingFromApi(schoolDetails) {
  if (!schoolDetails?._id) return schoolDetails;
  try {
    const res = await apiClient.post("/buildings/filter", {
      query: { _id: schoolDetails._id },
    });
    const fresh = Array.isArray(res.data) ? res.data[0] : null;
    if (fresh) {
      return {
        ...schoolDetails,
        ...fresh,
        // keep signed logo from session if filter returns raw path
        logo: schoolDetails.logo || fresh.logo,
        config: fresh.config || schoolDetails.config,
      };
    }
  } catch (e) {
    console.log(e);
  }
  return schoolDetails;
}

/**
 * @param {object} record
 * @param {object} settingsVuex
 * @param {object} user
 * @param {{ templateOverride?: object, output?: 'save'|'blob', stampUrl?: string|null, skipConfigRefresh?: boolean }} [options]
 * @returns {Promise<Blob|void>}
 */
export async function printInvoice(record, settingsVuex, user, options = {}) {
  let schoolDetails = (user?.building || []).find(
    (el) => el.dbName == settingsVuex.activeBuilding
  );

  // Always reload school config from Root so saved templates apply on Imprimer
  // (vuex / session can be stale after save on another page).
  if (!options.templateOverride && !options.skipConfigRefresh) {
    schoolDetails = await refreshBuildingFromApi(schoolDetails);
  }

  const docType = docTypeFromTypeFR(record.typeFR);
  const template =
    options.templateOverride ||
    resolveDocumentTemplate(schoolDetails, docType);

  let stampAsset = null;
  if (template.showStamp) {
    const stampUrl =
      options.stampUrl ?? (await loadStampUrl(schoolDetails, settingsVuex));
    stampAsset = await loadStampImageElement(stampUrl);
  }

  if (
    (template.showTuitionSummary || template.showActivitySummary) &&
    !record.paymentSummary
  ) {
    record = { ...record, paymentSummary: await loadPaymentSummary(record._id) };
  }
  if (template.showPaymentMethodDetails && needsPaymentDetailsReload(record)) {
    const details = await loadPaymentDetails(record._id);
    if (details) {
      record = {
        ...record,
        ...(Array.isArray(details.cheques) ? { cheques: details.cheques } : {}),
        ...(details.tpe ? { tpe: details.tpe } : {}),
      };
    }
  }

  if (record.typeFR == "F") {
    return await generateFacture(
      record,
      settingsVuex,
      schoolDetails,
      template,
      stampAsset,
      options
    );
  }
  return await generateRecu(
    record,
    settingsVuex,
    schoolDetails,
    template,
    stampAsset,
    options
  );
}

async function generateFacture(record, settings, schoolDetails, template, stampAsset, options = {}) {
  const fileName = `${i18n.t("invoice.facture")}_${
    record.numero || record.serial || ""
  }.pdf`;
  const dual = Number(template.copiesPerPage) === 2;

  const draw = (doc) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const isArabic = i18n.locale === "ar-AR";
  let y = 8;
  doc.__edtrustDrawFooter = () =>
    generateFooter(doc, schoolDetails, template, stampAsset);
  y = generateHeader(doc, record, settings, y, schoolDetails, template);
  generateFooter(doc, schoolDetails, template, stampAsset);

  doc.setFontSize(18);
  const titleText =
    record.cancellation && record.cancellation.type == "Avoir"
      ? i18n.t("invoice.factureAvoir")
      : i18n.t("invoice.facture");
  const titleX = isArabic ? pageWidth - 30 - doc.getTextWidth(titleText) : 40;
  doc.text(titleText, titleX, y);

  y = 150;
  const showBasePct =
    template.baseReductionDisplay === BASE_REDUCTION_DISPLAY.PERCENT_PER_LINE;
  const columns = [
    { header: i18n.t("invoice.nomPrenomEleve"), dataKey: "student" },
    { header: i18n.t("invoice.designation"), dataKey: "designation" },
    { header: i18n.t("invoice.frais"), dataKey: "fees" },
    ...(showBasePct
      ? [{ header: i18n.t("invoice.baseReductionShort"), dataKey: "red" }]
      : []),
    { header: i18n.t("invoice.quantite"), dataKey: "qte" },
    { header: i18n.t("invoice.prixUnitaireHT"), dataKey: "PU" },
    { header: i18n.t("invoice.montantHT"), dataKey: "montantHT" },
    { header: i18n.t("invoice.montantTTC"), dataKey: "montantTTC" },
  ];
  const array = [];
  let remaining = 0;
  (record.details || []).forEach((item) => {
    let paymentRow = [];
    const fees = Object.keys(item.fees || {});
    const rep = item.payment?.repReduction || {};

    paymentRow.push({
      content: item.studentName || "",
      rowSpan: fees.length || 1,
      styles: { valign: "middle", halign: "center" },
    });
    paymentRow.push({
      content: formatDesignation(item, template),
      rowSpan: fees.length || 1,
      styles: { valign: "middle", halign: "center" },
    });

    fees.forEach((feeKey, index) => {
      remaining += item.fees[feeKey].remaining || 0;
      const feeAmountHT =
        item.fees[feeKey].paid - item.fees[feeKey].paid * (item.tva || 0);
      paymentRow.push(
        formatFeeName(feeKey, item.daily, i18n.t.bind(i18n)) +
          (item.fees[feeKey].remaining ? i18n.t("invoice.avanceLabel") : "")
      );
      if (showBasePct) {
        const pct =
          item.fees[feeKey]?.reduction ??
          (typeof rep[feeKey] === "number" ? rep[feeKey] : 0);
        paymentRow.push(pct ? `${pct} %` : "—");
      }
      paymentRow.push("1");
      paymentRow.push(feeAmountHT.toFixed(3) || "");
      if (index == 0) {
        paymentRow.push({
          content: Number(item.montantHT || 0).toFixed(3) || "",
          rowSpan: fees.length,
          styles: { valign: "middle", halign: "center" },
        });
        paymentRow.push({
          content: Number(item.montantTotal || 0).toFixed(3) || "",
          rowSpan: fees.length,
          styles: { valign: "middle", halign: "center" },
        });
      }
      array.push(paymentRow);
      paymentRow = [];
    });
  });

  const finalColumns = isArabic ? [...columns].reverse() : columns;
  const finalArray = isArabic ? array.map((row) => [...row].reverse()) : array;
  const footerReservedHeight = getFooterReservedHeight(template);

  doc.autoTable({
    startY: y,
    head: [finalColumns.map((col) => col.header)],
    body: finalArray,
    styles: {
      fontSize: 9,
      font: "Amiri",
      halign: isArabic ? "right" : "left",
    },
    theme: "grid",
    headStyles: {
      fillColor: TABLE_HEAD_FILL,
      textColor: TABLE_HEAD_TEXT,
      halign: isArabic ? "right" : "left",
    },
    margin: { left: 30, right: 30, bottom: footerReservedHeight },
    pageBreak: "auto",
    didDrawPage: function (data) {
      generateFooter(doc, schoolDetails, template, stampAsset);
      if (data.pageNumber > 1) {
        doc.setFontSize(9);
        doc.setFont("Amiri", "normal");
        const pageText = `${i18n.t("invoice.page")} ${data.pageNumber}`;
        const pageX = isArabic
          ? pageWidth - 30 - doc.getTextWidth(pageText)
          : 30;
        doc.text(pageText, pageX, 20);
      }
    },
  });

  let totalsY = doc.lastAutoTable.finalY + 1;
  const pageHeight = doc.internal.pageSize.getHeight();
  if (totalsY + 120 > pageHeight - footerReservedHeight) {
    doc.addPage();
    generateFooter(doc, schoolDetails, template, stampAsset);
    totalsY = 20;
  }

  doc.setFontSize(10);
  const fontStyle = i18n.locale === "ar-AR" ? "normal" : "italic";
  doc.setFont("Amiri", fontStyle);
  const labelText = i18n.t("invoice.arreteePresenteFacture");
  const labelX = isArabic ? pageWidth - 30 - doc.getTextWidth(labelText) : 40;
  doc.text(labelText, labelX, totalsY + 30);
  doc.setFont("Amiri", "normal");

  const fiscalFlags = template.fiscal || {};
  const netAPayer =
    Number(record.montantRetenue) > 0 &&
    record.netApresRetenue != null &&
    Number.isFinite(Number(record.netApresRetenue))
      ? Number(record.netApresRetenue)
      : Number(record.montantTotal || 0);

  const montantEnLettres = numberToWords(netAPayer);
  const montantEnLettresCapitalized =
    montantEnLettres.charAt(0).toUpperCase() + montantEnLettres.slice(1);
  const amountX = isArabic
    ? pageWidth - 30 - doc.getTextWidth(montantEnLettresCapitalized || "")
    : 40;
  doc.text(montantEnLettresCapitalized || "", amountX, totalsY + 45);

  const bodyRows = [
    [
      i18n.t("invoice.totalHTBrut"),
      record.montantHtBrut ? Number(record.montantHtBrut).toFixed(3) : "",
    ],
  ];

  if (
    template.showOnTransReduction &&
    record.onTransReduction &&
    record.onTransReduction !== 0
  ) {
    bodyRows.push([
      i18n.t("invoice.remise"),
      Number(record.onTransReduction).toFixed(3) + "%",
    ]);
  }

  bodyRows.push(
    [i18n.t("invoice.totalHT"), Number(record.montantHT || 0).toFixed(3)]
  );

  if (fiscalFlags.showTva !== false) {
    bodyRows.push([
      i18n.t("invoice.totalTVA"),
      Number(record.montantTVA || 0).toFixed(3),
    ]);
  }

  if (
    fiscalFlags.showTimbre !== false &&
    record.fiscal &&
    record.fiscal !== 0
  ) {
    bodyRows.push([
      i18n.t("invoice.timbre"),
      Number(record.fiscal).toFixed(3),
    ]);
  }

  const montantRetenue = Number(record.montantRetenue || 0);
  if (fiscalFlags.showRetenue && (montantRetenue > 0 || record.retenuePercent)) {
    const pct = Number(record.retenuePercent || 0);
    bodyRows.push([
      pct
        ? `${i18n.t("invoice.retenueALaSource")} (${pct}%)`
        : i18n.t("invoice.retenueALaSource"),
      montantRetenue.toFixed(3),
    ]);
  }

  bodyRows.push([i18n.t("invoice.netAPayer"), netAPayer.toFixed(3)]);
  if (remaining > 0) {
    bodyRows.push([i18n.t("invoice.reste"), remaining.toFixed(3)]);
  }

  const finalBodyRows = isArabic
    ? bodyRows.map((row) => [row[1], row[0]])
    : bodyRows;

  doc.autoTable({
    startY: totalsY,
    head: [["", ""]],
    body: finalBodyRows,
    styles: {
      fontSize: 9,
      font: "Amiri",
      halign: isArabic ? "right" : "left",
    },
    margin: {
      left: isArabic ? 30 : pageWidth - 200,
      right: isArabic ? pageWidth - 200 : 30,
      bottom: footerReservedHeight,
    },
    tableWidth: 170,
    theme: "grid",
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: 255,
      lineWidth: 0,
      halign: isArabic ? "right" : "left",
    },
  });

  let extraY = doc.lastAutoTable.finalY + 10;
  extraY = ensureSpace(doc, schoolDetails, template, stampAsset, 80, extraY);
  if (template.baseReductionDisplay === BASE_REDUCTION_DISPLAY.TOTAL) {
    extraY = drawBaseReductionBlock(
      doc,
      record,
      template,
      extraY,
      isArabic,
      pageWidth
    );
  }
  extraY = ensureSpace(doc, schoolDetails, template, stampAsset, 80, extraY);
  extraY = drawPaymentMethodBlock(
    doc,
    record,
    template,
    extraY,
    isArabic,
    pageWidth
  );
  extraY = ensureSpace(doc, schoolDetails, template, stampAsset, 80, extraY);
  extraY = drawPaymentSummary(doc, record, template, extraY, isArabic, pageWidth);
  drawTableNote(doc, template, extraY, isArabic, pageWidth);
  // Stamp / signature only on the last page (not on page 1 of multi-page docs)
  drawStampAndSignature(doc, schoolDetails, template, stampAsset);
  }; // end draw

  if (dual) {
    const land = await composeLandscapeDualCopies(draw);
    return finishDoc(land, fileName, options);
  }
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  draw(doc);
  return finishDoc(doc, fileName, options);
}

async function generateRecu(record, settings, schoolDetails, template, stampAsset, options = {}) {
  const fileName = `${i18n.t("invoice.recu")}_${record.serial || ""}.pdf`;
  const dual = Number(template.copiesPerPage) === 2;

  const draw = (doc) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const isArabic = i18n.locale === "ar-AR";
  let y = 8;
  doc.__edtrustDrawFooter = () =>
    generateFooter(doc, schoolDetails, template, stampAsset);
  y = generateHeader(doc, record, settings, y, schoolDetails, template);
  generateFooter(doc, schoolDetails, template, stampAsset);

  doc.setFontSize(18);
  const titleText =
    record.cancellation && record.cancellation.type == "Avoir"
      ? i18n.t("invoice.recuAvoir")
      : i18n.t("invoice.recu");
  const titleX = isArabic ? pageWidth - 30 - doc.getTextWidth(titleText) : 40;
  doc.text(titleText, titleX, y);
  y = 150;

  const showBasePct =
    template.baseReductionDisplay === BASE_REDUCTION_DISPLAY.PERCENT_PER_LINE;
  const columns = [
    { header: i18n.t("invoice.nomPrenomEleve"), dataKey: "student" },
    { header: i18n.t("invoice.designation"), dataKey: "designation" },
    { header: i18n.t("invoice.frais"), dataKey: "fees" },
    ...(showBasePct
      ? [{ header: i18n.t("invoice.baseReductionShort"), dataKey: "red" }]
      : []),
    { header: i18n.t("invoice.qte"), dataKey: "qte" },
    { header: i18n.t("invoice.prixUnitaire"), dataKey: "pu" },
    { header: i18n.t("invoice.total"), dataKey: "total" },
  ];
  const array = [];
  let remaining = 0;
  (record.details || []).forEach((item) => {
    let paymentRow = [];
    const fees = Object.keys(item.fees || {});
    const rep = item.payment?.repReduction || {};

    paymentRow.push({
      content: item.studentName || "",
      rowSpan: fees.length || 1,
      styles: { valign: "middle", halign: "center" },
    });
    paymentRow.push({
      content: formatDesignation(item, template),
      rowSpan: fees.length || 1,
      styles: { valign: "middle", halign: "center" },
    });

    fees.forEach((feeKey, index) => {
      paymentRow.push(
        formatFeeName(feeKey, item.daily, i18n.t.bind(i18n)) +
          (item.fees[feeKey].remaining ? i18n.t("invoice.avanceLabel") : "")
      );
      if (showBasePct) {
        const pct =
          item.fees[feeKey]?.reduction ??
          (typeof rep[feeKey] === "number" ? rep[feeKey] : 0);
        paymentRow.push(pct ? `${pct} %` : "—");
      }
      paymentRow.push("1");
      remaining += item.fees[feeKey].remaining || 0;
      paymentRow.push(Number(item.fees[feeKey].paid || 0).toFixed(3));
      if (index == 0) {
        paymentRow.push({
          content: Number(item.montantTotal || 0).toFixed(3),
          rowSpan: fees.length,
          styles: { valign: "middle", halign: "center" },
        });
      }
      array.push(paymentRow);
      paymentRow = [];
    });
  });

  const finalColumns = isArabic ? [...columns].reverse() : columns;
  const finalArray = isArabic ? array.map((row) => [...row].reverse()) : array;
  const footerReservedHeight = getFooterReservedHeight(template);

  doc.autoTable({
    startY: y,
    head: [finalColumns.map((col) => col.header)],
    body: finalArray,
    styles: {
      fontSize: 9,
      font: "Amiri",
      halign: isArabic ? "right" : "left",
    },
    theme: "grid",
    headStyles: {
      fillColor: TABLE_HEAD_FILL,
      textColor: TABLE_HEAD_TEXT,
      halign: isArabic ? "right" : "left",
    },
    margin: { left: 30, right: 30, bottom: footerReservedHeight },
    pageBreak: "auto",
    didDrawPage: function (data) {
      generateFooter(doc, schoolDetails, template, stampAsset);
      if (data.pageNumber > 1) {
        doc.setFontSize(9);
        doc.setFont("Amiri", "normal");
        const pageText = `${i18n.t("invoice.page")} ${data.pageNumber}`;
        const pageX = isArabic
          ? pageWidth - 30 - doc.getTextWidth(pageText)
          : 20;
        doc.text(pageText, pageX, 20);
      }
    },
  });

  let summaryY = doc.lastAutoTable.finalY + 1;
  const pageHeight = doc.internal.pageSize.getHeight();
  if (summaryY + 60 > pageHeight - footerReservedHeight) {
    doc.addPage();
    generateFooter(doc, schoolDetails, template, stampAsset);
    summaryY = 20;
  }

  doc.setFontSize(10);
  const fontStyle = i18n.locale === "ar-AR" ? "normal" : "italic";
  doc.setFont("Amiri", fontStyle);
  const labelText = i18n.t("invoice.arreteePresenteFacture");
  const labelX = isArabic ? pageWidth - 30 - doc.getTextWidth(labelText) : 40;
  doc.text(labelText, labelX, summaryY + 30);
  doc.setFont("Amiri", "normal");
  const montantEnLettres = numberToWords(record.montantTotal);
  const montantEnLettresCapitalized =
    montantEnLettres.charAt(0).toUpperCase() + montantEnLettres.slice(1);
  const amountX = isArabic
    ? pageWidth - 30 - doc.getTextWidth(montantEnLettresCapitalized || "")
    : 40;
  doc.text(montantEnLettresCapitalized || "", amountX, summaryY + 45);

  const summaryBodyRows = [
    [i18n.t("invoice.total"), Number(record.montantTotal || 0).toFixed(3)],
  ];

  if (
    template.showOnTransReduction &&
    record.onTransReduction &&
    record.onTransReduction !== 0
  ) {
    summaryBodyRows.push([
      i18n.t("invoice.remise"),
      Number(record.onTransReduction).toFixed(3) + "%",
    ]);
  }

  if (remaining > 0) {
    summaryBodyRows.push([i18n.t("invoice.reste"), remaining.toFixed(3)]);
  }

  const finalSummaryBodyRows = isArabic
    ? summaryBodyRows.map((row) => [row[1], row[0]])
    : summaryBodyRows;

  doc.autoTable({
    startY: summaryY,
    head: [["", ""]],
    body: finalSummaryBodyRows,
    styles: {
      fontSize: 9,
      font: "Amiri",
      halign: isArabic ? "right" : "left",
    },
    margin: {
      left: isArabic ? 30 : pageWidth - 140,
      right: isArabic ? pageWidth - 140 : 30,
      bottom: footerReservedHeight,
    },
    tableWidth: 110,
    theme: "grid",
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: 255,
      lineWidth: 0,
      halign: isArabic ? "right" : "left",
    },
  });

  let extraY = doc.lastAutoTable.finalY + 10;
  extraY = ensureSpace(doc, schoolDetails, template, stampAsset, 80, extraY);
  if (template.baseReductionDisplay === BASE_REDUCTION_DISPLAY.TOTAL) {
    extraY = drawBaseReductionBlock(
      doc,
      record,
      template,
      extraY,
      isArabic,
      pageWidth
    );
  }
  extraY = ensureSpace(doc, schoolDetails, template, stampAsset, 80, extraY);
  extraY = drawPaymentMethodBlock(
    doc,
    record,
    template,
    extraY,
    isArabic,
    pageWidth
  );
  extraY = ensureSpace(doc, schoolDetails, template, stampAsset, 80, extraY);
  extraY = drawPaymentSummary(doc, record, template, extraY, isArabic, pageWidth);
  drawTableNote(doc, template, extraY, isArabic, pageWidth);
  // Stamp / signature only on the last page (not on page 1 of multi-page docs)
  drawStampAndSignature(doc, schoolDetails, template, stampAsset);
  }; // end draw

  if (dual) {
    const land = await composeLandscapeDualCopies(draw);
    return finishDoc(land, fileName, options);
  }
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  draw(doc);
  return finishDoc(doc, fileName, options);
}
